import PropTypes from "prop-types";
import { InputAdornment, TextField } from "@mui/material";
import get from "lodash.get";

import { REQUIRED_MESSAGE } from "../constants/formConstants";

const setNumberValue = (value) => {
  return value === "" ? undefined : Number(value);
};

/**
 * @param {string|number} [currentValue] current value of the input
 * @param {string} [endAdornment] string to display at the end of the input like '%' or '$'
 * @param {object} errors object containing errors from react-hook-form
 * @param {object} inputProps object containing props for the MUI input component
 * @param {boolean} [isRequired] whether the input is required
 * @param {number} [max] maximum value for a number input
 * @param {number} [min] minimum value for a number input
 * @param {string} name name of the input
 * @param {function} register function from react-hook-form
 * @param {string} [startAdornment] string to display at the start of the input like '%' or '$'
 * @param {string} [step] step for a number input
 * @param {string} [type] type of input. For example, 'text' or 'number'
 * @param {object|function} [validate] used to validate the input
 * @returns
 */
export default function TextInput({
  currentValue,
  endAdornment,
  errors,
  inputProps,
  isRequired = false,
  max,
  min,
  name,
  register,
  startAdornment,
  step = "any",
  type = "text",
  validate,
  ...restProps
}) {
  const error = get(errors, name);

  const getFormattedCurrencyAmount = () => {
    if (currentValue === undefined) {
      return undefined;
    }
    return currentValue > 9999
      ? (startAdornment ? startAdornment + " " : "") +
          currentValue?.toLocaleString()
      : " ";
  };

  return (
    <TextField
      autoComplete="off"
      error={!!error}
      helperText={error?.message ?? getFormattedCurrencyAmount()}
      InputProps={{
        endAdornment: endAdornment ? (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ) : null,
        startAdornment: startAdornment ? (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ) : null,
        inputProps: { ...inputProps, max, min, step },
      }}
      onWheel={(e) => e.target.blur()} // prevent user from accidentally changing the value when trying to scroll
      type={type}
      {...restProps}
      {...register(name, {
        max:
          max || max === 0
            ? { value: max, message: `Maximum: ${max.toLocaleString()}` }
            : null,
        min:
          min || min === 0
            ? { value: min, message: `Minimum: ${min.toLocaleString()}` }
            : null,
        required: isRequired ? REQUIRED_MESSAGE : null,
        setValueAs: type === "number" ? setNumberValue : null,
        validate,
      })}
    />
  );
}

TextInput.propTypes = {
  currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // this prop gets passed to TextField through ...restProps
  endAdornment: PropTypes.node,
  errors: PropTypes.object.isRequired,
  inputProps: PropTypes.object,
  isRequired: PropTypes.bool,
  max: PropTypes.number,
  min: PropTypes.number,
  name: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  startAdornment: PropTypes.node,
  step: PropTypes.string,
  type: PropTypes.oneOf(["text", "number"]),
  validate: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};
